import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthSession, bookingApi, reviewApi } from '../services/api';
import '../styles/client-panel.css';

function SessionHistoryPage() {
  const navigate = useNavigate();
  const authSession = getAuthSession();

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingReviewId, setEditingReviewId] = useState(null);
  const [reviewForm, setReviewForm] = useState({ rating: 5, review: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!authSession?.token) {
      navigate('/login');
      return;
    }
    const fetchHistory = async () => {
      try {
        setLoading(true);
        const res = await bookingApi.mine();
        const rawBookings = res.bookings || res || [];
        setSessions(rawBookings);
      } catch (err) {
        console.error('Failed to load session history:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [authSession?.token, navigate]);

  const handleStartReview = (session) => {
    setEditingReviewId(session._id || session.id);
    setReviewForm({ rating: session.rating || 5, review: session.review || '' });
  };

  const handleSaveReview = async (bookingId) => {
    try {
      setSubmitting(true);
      await reviewApi.createReview({
        bookingId,
        rating: reviewForm.rating,
        comment: reviewForm.review,
      });
      setSessions(sessions.map(s => 
        (s._id || s.id) === bookingId 
          ? { ...s, rating: reviewForm.rating, review: reviewForm.review, hasReviewed: true }
          : s
      ));
      setEditingReviewId(null);
      alert('Review saved successfully!');
    } catch (err) {
      alert(err.message || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelReview = () => {
    setEditingReviewId(null);
    setReviewForm({ rating: 5, review: '' });
  };

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '40px 20px' }}>
      <button onClick={() => navigate('/client-dashboard')} style={{
        marginBottom: '30px',
        padding: '8px 16px',
        background: 'white',
        border: '1px solid #7c3aed',
        color: '#7c3aed',
        borderRadius: '8px',
        cursor: 'pointer',
        fontWeight: 600
      }}>
        ← Back to Dashboard
      </button>

      <h1 style={{ fontSize: '2rem', color: '#1a0033', marginBottom: '30px' }}>📋 Session History</h1>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {sessions.map(session => (
          <div key={session.id} style={{
            background: 'white',
            border: '1px solid #e8dff5',
            borderRadius: '12px',
            padding: '24px',
            transition: 'box-shadow 0.2s'
          }}>
            {/* Header */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '20px', marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #f3eaff' }}>
              <div>
                <h3 style={{ margin: '0 0 8px 0', color: '#1a0033', fontSize: '1.2rem' }}>
                  {session.therapistName}
                </h3>
                <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '0.95rem' }}>
                  {session.specialization}
                </p>
                <p style={{ margin: '0', color: '#999', fontSize: '0.9rem' }}>
                  📅 {session.date} at {session.time} • {session.duration} • {session.mode === 'online' ? '🌐 Online' : '🏢 In-Person'}
                </p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{
                  display: 'inline-block',
                  padding: '6px 12px',
                  background: '#e0f7ea',
                  color: '#28a745',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: 600
                }}>
                  ✅ Completed
                </span>
              </div>
            </div>

            {/* Session Notes */}
            {session.notes && (
              <div style={{ marginBottom: '20px', padding: '12px', background: '#faf5fd', borderLeft: '3px solid #d8b4fe', borderRadius: '4px' }}>
                <p style={{ margin: '0 0 4px 0', fontSize: '0.85rem', color: '#6b7280', fontWeight: 600 }}>Session Notes:</p>
                <p style={{ margin: 0, color: '#1a0033', fontSize: '0.95rem' }}>{session.notes}</p>
              </div>
            )}

            {/* Review Section */}
            {editingReviewId === session.id ? (
              <div style={{ background: '#faf5fd', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
                <h4 style={{ margin: '0 0 16px 0', color: '#1a0033' }}>Leave a Review</h4>

                {/* Star Rating */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#1a0033' }}>Rating</label>
                  <div style={{ display: 'flex', gap: '8px', fontSize: '1.8rem' }}>
                    {[1, 2, 3, 4, 5].map(star => (
                      <button
                        key={star}
                        onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '1.8rem',
                          opacity: star <= reviewForm.rating ? 1 : 0.3,
                          transition: 'opacity 0.2s'
                        }}
                      >
                        ⭐
                      </button>
                    ))}
                  </div>
                </div>

                {/* Review Text */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#1a0033' }}>Your Review</label>
                  <textarea 
                    value={reviewForm.review}
                    onChange={e => setReviewForm({ ...reviewForm, review: e.target.value })}
                    placeholder="Share your feedback about this session..."
                    style={{
                      width: '100%',
                      minHeight: '100px',
                      padding: '12px',
                      border: '1px solid #e8dff5',
                      borderRadius: '8px',
                      fontFamily: 'inherit',
                      fontSize: '0.95rem',
                      resize: 'vertical'
                    }}
                  />
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={() => handleSaveReview(session.id)}
                    style={{
                      padding: '10px 16px',
                      background: '#7c3aed',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    ✅ Save Review
                  </button>
                  <button
                    onClick={handleCancelReview}
                    style={{
                      padding: '10px 16px',
                      background: 'white',
                      color: '#7c3aed',
                      border: '1px solid #7c3aed',
                      borderRadius: '8px',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    ❌ Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div>
                {session.rating > 0 ? (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <span style={{ fontSize: '1.2rem' }}>{'⭐'.repeat(session.rating)}</span>
                      <span style={{ color: '#666', fontSize: '0.95rem' }}>({session.rating}/5)</span>
                    </div>
                    <p style={{ margin: 0, color: '#1a0033', fontSize: '0.95rem', lineHeight: 1.5 }}>
                      {session.review}
                    </p>
                  </div>
                ) : (
                  <p style={{ margin: '0 0 16px 0', color: '#999', fontSize: '0.95rem', fontStyle: 'italic' }}>
                    No review yet
                  </p>
                )}

                <button
                  onClick={() => handleStartReview(session)}
                  style={{
                    padding: '10px 16px',
                    background: session.rating > 0 ? 'white' : '#f3eaff',
                    color: '#7c3aed',
                    border: '1px solid #d8b4fe',
                    borderRadius: '8px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  {session.rating > 0 ? '✏️ Edit Review' : '📝 Write Review'}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {sessions.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: '#999'
        }}>
          <p style={{ fontSize: '1.1rem' }}>No session history yet</p>
          <p>Book a session with a therapist to get started!</p>
        </div>
      )}
    </div>
  );
}

export default SessionHistoryPage;
